/**
 * ClientBillingTab Component
 *
 * Shows a client's invoices, subscriptions, and payment history.
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreVertical,
  Copy,
  ExternalLink,
  CheckCircle,
  XCircle,
  Receipt,
  CreditCard,
  Settings,
  Repeat,
  Pause,
  Play,
} from "lucide-react";
import {
  useInvoices,
  Invoice,
  InvoiceStatus,
  INVOICE_STATUS_CONFIG,
} from "@/hooks/use-invoices";
import {
  useClientSubscriptions,
  ClientSubscription,
  SubscriptionStatus,
  SUBSCRIPTION_STATUS_CONFIG,
  formatBillingInterval,
} from "@/hooks/use-client-subscriptions";
import { CreateInvoiceDialog } from "@/components/invoices/CreateInvoiceDialog";
import { CreateSubscriptionDialog } from "@/components/subscriptions/CreateSubscriptionDialog";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useStripeConnect } from "@/hooks/use-stripe-connect";
import { useNavigate } from "react-router-dom";

interface ClientBillingTabProps {
  clientId: string;
  clientName: string;
}

export function ClientBillingTab({ clientId, clientName }: ClientBillingTabProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [isCreateSubscriptionOpen, setIsCreateSubscriptionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("invoices");

  const { isConnected, isPending, loading: stripeLoading } = useStripeConnect();
  const { invoices, loading: loadingInvoices, stats: invoiceStats, refetch: refetchInvoices, cancelInvoice, markAsPaid } = useInvoices({
    clientId,
  });
  const {
    subscriptions,
    loading: loadingSubscriptions,
    stats: subscriptionStats,
    refetch: refetchSubscriptions,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
  } = useClientSubscriptions({ clientId });

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied",
      description: "Payment link copied to clipboard",
    });
  };

  const getInvoiceStatusBadge = (status: InvoiceStatus) => {
    const config = INVOICE_STATUS_CONFIG[status];
    return (
      <Badge
        variant="outline"
        className={cn("font-medium", config.color, config.bgColor)}
      >
        {config.label}
      </Badge>
    );
  };

  const getSubscriptionStatusBadge = (status: SubscriptionStatus) => {
    const config = SUBSCRIPTION_STATUS_CONFIG[status];
    return (
      <Badge
        variant="outline"
        className={cn("font-medium", config.color, config.bgColor)}
      >
        {config.label}
      </Badge>
    );
  };

  const loading = loadingInvoices || loadingSubscriptions;

  if (loading || stripeLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted/50 rounded-lg animate-pulse" />
        <div className="h-64 bg-muted/50 rounded-lg animate-pulse" />
      </div>
    );
  }

  // Show setup prompt if Stripe Connect is not configured
  if (!isConnected) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <CreditCard className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">
            {isPending ? "Complete Your Stripe Setup" : "Connect Stripe to Create Invoices"}
          </h3>
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            {isPending
              ? "Finish setting up Stripe Connect to start creating invoices for this client."
              : "Connect your Stripe account to create invoices and accept payments directly from clients."}
          </p>
          <Button onClick={() => navigate("/settings?section=billing")} className="gap-2">
            <Settings className="h-4 w-4" />
            {isPending ? "Complete Stripe Setup" : "Set Up Stripe Connect"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(invoiceStats.totalOutstanding)}
            </div>
            <p className="text-xs text-muted-foreground">
              {invoiceStats.pending} unpaid invoices
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
            <CreditCard className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {formatCurrency(invoiceStats.totalPaid)}
            </div>
            <p className="text-xs text-muted-foreground">
              {invoiceStats.paid} paid invoices
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <Repeat className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {subscriptionStats.active}
            </div>
            <p className="text-xs text-muted-foreground">
              {subscriptions.length} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Recurring</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(subscriptionStats.monthlyRecurring)}
            </div>
            <p className="text-xs text-muted-foreground">
              from active subscriptions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Invoices and Subscriptions */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="invoices" className="gap-2">
            <Receipt className="h-4 w-4" />
            Invoices
            {invoiceStats.pending > 0 && (
              <Badge variant="secondary" className="ml-1">
                {invoiceStats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-2">
            <Repeat className="h-4 w-4" />
            Subscriptions
            {subscriptionStats.active > 0 && (
              <Badge variant="secondary" className="ml-1">
                {subscriptionStats.active}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Invoices</CardTitle>
                <CardDescription>
                  Manage invoices for {clientName}
                </CardDescription>
              </div>
              <Button onClick={() => setIsCreateInvoiceOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                New Invoice
              </Button>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No invoices yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create an invoice to start accepting payments from this client.
                  </p>
                  <Button onClick={() => setIsCreateInvoiceOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create First Invoice
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{invoice.invoice_number}</p>
                            <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                              {invoice.description || "No description"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatCurrency(invoice.total_cents)}
                          </span>
                        </TableCell>
                        <TableCell>{getInvoiceStatusBadge(invoice.status)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{format(new Date(invoice.created_at), "MMM d, yyyy")}</p>
                            <p className="text-muted-foreground text-xs">
                              {formatDistanceToNow(new Date(invoice.created_at), {
                                addSuffix: true,
                              })}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {invoice.payment_link_url && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      window.open(invoice.payment_link_url!, "_blank")
                                    }
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Open Payment Link
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleCopyLink(invoice.payment_link_url!)}
                                  >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy Payment Link
                                  </DropdownMenuItem>
                                </>
                              )}
                              {["pending", "sent", "overdue"].includes(invoice.status) && (
                                <DropdownMenuItem onClick={() => markAsPaid(invoice.id)}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Mark as Paid
                                </DropdownMenuItem>
                              )}
                              {["draft", "pending", "sent"].includes(invoice.status) && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => cancelInvoice(invoice.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Cancel Invoice
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Subscriptions</CardTitle>
                <CardDescription>
                  Manage recurring billing for {clientName}
                </CardDescription>
              </div>
              <Button onClick={() => setIsCreateSubscriptionOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                New Subscription
              </Button>
            </CardHeader>
            <CardContent>
              {subscriptions.length === 0 ? (
                <div className="text-center py-8">
                  <Repeat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No subscriptions yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create a subscription for automatic recurring billing.
                  </p>
                  <Button onClick={() => setIsCreateSubscriptionOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create Subscription
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Next Billing</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{subscription.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatBillingInterval(subscription.interval, subscription.interval_count)}
                              {subscription.sessions_per_period && (
                                <> &middot; {subscription.sessions_per_period} sessions/period</>
                              )}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatCurrency(subscription.amount_cents)}
                          </span>
                        </TableCell>
                        <TableCell>{getSubscriptionStatusBadge(subscription.status)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {subscription.current_period_end ? (
                              <>
                                <p>{format(new Date(subscription.current_period_end), "MMM d, yyyy")}</p>
                                <p className="text-muted-foreground text-xs">
                                  {formatDistanceToNow(new Date(subscription.current_period_end), {
                                    addSuffix: true,
                                  })}
                                </p>
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                            {subscription.cancel_at_period_end && (
                              <Badge variant="outline" className="text-warning mt-1">
                                Cancels at period end
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {subscription.status === "paused" ? (
                                <DropdownMenuItem onClick={() => resumeSubscription(subscription.id)}>
                                  <Play className="h-4 w-4 mr-2" />
                                  Resume Subscription
                                </DropdownMenuItem>
                              ) : subscription.status === "active" || subscription.status === "trialing" ? (
                                <DropdownMenuItem onClick={() => pauseSubscription(subscription.id)}>
                                  <Pause className="h-4 w-4 mr-2" />
                                  Pause Subscription
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuSeparator />
                              {!subscription.cancel_at_period_end &&
                                ["active", "trialing", "past_due"].includes(subscription.status) && (
                                  <DropdownMenuItem
                                    onClick={() => cancelSubscription(subscription.id, false)}
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Cancel at Period End
                                  </DropdownMenuItem>
                                )}
                              {["active", "trialing", "paused", "past_due"].includes(subscription.status) && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => cancelSubscription(subscription.id, true)}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Cancel Immediately
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={isCreateInvoiceOpen}
        onOpenChange={setIsCreateInvoiceOpen}
        preselectedClientId={clientId}
        onSuccess={() => {
          setIsCreateInvoiceOpen(false);
          refetchInvoices();
        }}
      />

      {/* Create Subscription Dialog */}
      <CreateSubscriptionDialog
        open={isCreateSubscriptionOpen}
        onOpenChange={setIsCreateSubscriptionOpen}
        preselectedClientId={clientId}
        onSuccess={() => {
          setIsCreateSubscriptionOpen(false);
          refetchSubscriptions();
        }}
      />
    </div>
  );
}
